import React, { useState } from 'react';
import {
  BookOpen,
  Sparkles,
  RefreshCw,
  CheckCircle2,
  XCircle,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  HelpCircle,
} from 'lucide-react';
import { api } from '../services/api';
import './FlashCards.css';

const FlashCards = ({ pdfData }) => {
  const [flashcards, setFlashcards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [numCards, setNumCards] = useState(10);
  const [showCards, setShowCards] = useState(false);
  const [error, setError] = useState(null);
  const [cardResults, setCardResults] = useState({});

  const generateFlashCards = async () => {
    if (!pdfData || !pdfData.text) {
      setError('Nenhum PDF carregado');
      return;
    }

    setLoading(true);
    setError(null);
    setIsFlipped(false);
    setCurrentIndex(0);
    setCardResults({});

    try {
      const response = await api.post('/api/generate-flashcards', {
        fileId: pdfData.fileId,
        text: pdfData.text,
        numCards: Math.min(Math.max(parseInt(numCards) || 10, 5), 20),
      });

      if (response.data.error) {
        setError(response.data.error);
        setLoading(false);
        return;
      }

      if (response.data.flashcards && response.data.flashcards.length > 0) {
        setFlashcards(response.data.flashcards);
        setShowCards(true);
        setCurrentIndex(0);
      } else {
        setError('Nenhum flashcard foi gerado');
      }
    } catch (err) {
      console.error('Erro ao gerar flashcards:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Erro ao gerar flashcards';
      
      if (errorMessage.includes('503') || errorMessage.includes('overloaded') || errorMessage.includes('UNAVAILABLE')) {
        setError('O serviço de IA está temporariamente sobrecarregado. Por favor, tente novamente em alguns instantes.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleNext = () => {
    if (currentIndex < flashcards.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsFlipped(false);
    }
  };

  const handleMarkResult = (resultType) => {
    setCardResults((prev) => ({
      ...prev,
      [currentIndex]: resultType,
    }));

    if (currentIndex < flashcards.length - 1) {
      setTimeout(() => {
        setCurrentIndex((prevIndex) =>
          Math.min(prevIndex + 1, flashcards.length - 1)
        );
        setIsFlipped(false);
      }, 240);
    }
  };

  const handleReset = () => {
    setShowCards(false);
    setFlashcards([]);
    setCurrentIndex(0);
    setIsFlipped(false);
    setCardResults({});
  };

  const currentCard = flashcards[currentIndex];
  const progress = flashcards.length > 0
    ? Math.round(((currentIndex + 1) / flashcards.length) * 100)
    : 0;
  const correctCount = Object.values(cardResults).filter(
    (status) => status === 'correct'
  ).length;
  const wrongCount = Object.values(cardResults).filter(
    (status) => status === 'wrong'
  ).length;
  const reviewedCount = Object.keys(cardResults).length;
  const currentCardStatus = cardResults[currentIndex];

  return (
    <div className="flashcards-container">
      <div className="flashcards-header">
        <h3>
          <BookOpen size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Flash Cards
        </h3>
        <p>Crie flashcards para estudar o conteúdo do PDF</p>
      </div>

      {!showCards ? (
        <div className="flashcards-generate">
          <div className="generate-controls">
            <label htmlFor="num-cards">Número de flashcards:</label>
            <input
              id="num-cards"
              type="number"
              min="5"
              max="20"
              value={numCards}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setNumCards('');
                  return;
                }
                const numValue = parseInt(value);
                if (!isNaN(numValue)) {
                  if (numValue > 20) {
                    setNumCards(20);
                  } else {
                    setNumCards(value);
                  }
                }
              }}
              onBlur={(e) => {
                const value = parseInt(e.target.value);
                if (isNaN(value) || value < 5) {
                  setNumCards(5);
                } else if (value > 20) {
                  setNumCards(20);
                }
              }}
              className="num-cards-input"
            />
          </div>
          <button
            onClick={generateFlashCards}
            disabled={loading}
            className="generate-button"
          >
            {loading ? (
              <>
                <RefreshCw size={18} className="spinning-icon" style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Gerando flashcards...
              </>
            ) : (
              <>
                <Sparkles size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Gerar Flash Cards
              </>
            )}
          </button>
          {error && <div className="error-message">{error}</div>}
        </div>
      ) : (
        <div className="flashcards-viewer">
          <div className="flashcards-stats">
            <span>
              <BarChart3 size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
              Card {currentIndex + 1} / {flashcards.length}
            </span>
            <span>
              <CheckCircle2 size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
              Acertos: {correctCount}
            </span>
            <span>
              <XCircle size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
              Erros: {wrongCount}
            </span>
          </div>

          <div className="flashcards-progress-bar">
            <div
              className="flashcards-progress-fill" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>

          <div 
            className={`flashcard ${isFlipped ? 'flipped' : ''}`}
            onClick={handleFlip}
          >
            <div className="flashcard-inner">
              <div className="flashcard-front">
                <div className="flashcard-label">
                  <HelpCircle size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  PERGUNTA
                </div>
                <div className="flashcard-content">
                  {currentCard?.front || 'Carregando...'}
                </div>
                <div className="flashcard-hint">Clique para ver a resposta</div>
              </div>
              <div className="flashcard-back">
                <div className="flashcard-label">
                  <Lightbulb size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  RESPOSTA
                </div>
                <div className="flashcard-content">
                  {currentCard?.back || 'Carregando...'}
                </div>
                <div className="flashcard-hint">Clique para ver a pergunta</div>
              </div>
            </div>
            {currentCardStatus ? (
              <div
                className={`flashcard-result ${currentCardStatus === 'correct' ? 'is-correct' : 'is-wrong'}`}
              >
                {currentCardStatus === 'correct' ? 'Acertei' : 'Errei'}
              </div>
            ) : null}
          </div>

          <div className="flashcards-controls">
            <button
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className="nav-button"
            >
              <ChevronLeft size={18} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
              Anterior
            </button>
            
            <div className="card-counter">
              {currentIndex + 1} / {flashcards.length}
            </div>

            <button
              onClick={handleNext}
              disabled={currentIndex === flashcards.length - 1}
              className="nav-button"
            >
              Próxima
              <ChevronRight size={18} style={{ marginLeft: '6px', verticalAlign: 'middle' }} />
            </button>
          </div>

          <div className="flashcards-actions">
            <button
              onClick={() => handleMarkResult('correct')}
              className={`result-button is-correct ${currentCardStatus === 'correct' ? 'selected' : ''}`}
            >
              <CheckCircle2 size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              Acertei
            </button>
            <button
              onClick={() => handleMarkResult('wrong')}
              className={`result-button is-wrong ${currentCardStatus === 'wrong' ? 'selected' : ''}`}
            >
              <XCircle size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              Errei
            </button>
            <button
              onClick={handleReset}
              className="reset-button"
            >
              <RefreshCw size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              Gerar Novos Flashcards
            </button>
          </div>

          <div className="flashcards-review-summary">
            Revisados: {reviewedCount} / {flashcards.length}
          </div>
        </div>
      )}

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Gerando flashcards com IA...</p>
        </div>
      )}
    </div>
  );
};

export default FlashCards;
